// FAQ copy for each page. Answers only restate facts already published on the
// site (what's included, platforms, timeline) and the prices in data/prices.ts.
// Ownership and refund answers follow Alan's 2026-09-28 decisions (client owns
// the domain, RareWorks owns a managed site until it's bought, no refunds).
// Do not add other contract or cancellation claims without confirming them.

export type FaqItem = { q: string; a: string };

export const homeFaqs: FaqItem[] = [
  {
    q: "What does Rare Works do?",
    a: "Rare Works is a small studio in Columbia, MS. We build and manage websites for small businesses, run social media, and design logos, ads, and print pieces. We also make our own software, including Skeulur for employee scheduling.",
  },
  {
    q: "How much does a website cost?",
    a: "A one-page starter site is $400, or $75 a month managed with no build fee. A business site with up to 10 pages is $1,800, or $150 a month managed. Every monthly plan starts with 30 days free. Our prices page lists everything.",
  },
  {
    q: "Where are you located?",
    a: "We're based in Columbia, MS and work with small businesses across South Mississippi. If you're farther away, get in touch anyway. We're happy to talk.",
  },
  {
    q: "Do you use WordPress or website templates?",
    a: "No. Every site is custom designed and hand-coded, with no WordPress, Squarespace, or drag-and-drop builders.",
  },
];

export const webDesignFaqs: FaqItem[] = [
  {
    q: "How much does a small business website cost?",
    a: "A one-page starter site is $400, or $75 a month managed with no build fee. A business site with up to 10 pages is $1,800, or $150 a month managed. Extra pages are $150 each, and sites over 10 pages are quoted on scope.",
  },
  {
    q: "How long does it take to build a website?",
    a: "It depends on the size of the site and how quickly we get what we need from you. We'll tell you what to expect when we talk.",
  },
  {
    q: "What do I need to send you?",
    a: "Whatever you have: photos, logos, and text. If you don't have any of it, we can create it from scratch. Either works.",
  },
  {
    q: "Do you use WordPress or templates?",
    a: "No. Every site is custom designed and hand-coded, with no WordPress, Squarespace, or drag-and-drop builders.",
  },
  {
    q: "Will people be able to find my website on Google?",
    a: "We set up the SEO basics on every site: page titles and descriptions, a sitemap, and Google Search Console. Nobody can promise a ranking, but for our clients we go further, with a page for each service and each town they serve.",
  },
  {
    q: "Can I make changes after my site launches?",
    a: "Yes. On a managed site or a care plan you send us the change and we make it, so you never have to touch code. A managed starter site includes up to 2 hours of changes a month.",
  },
  {
    q: "Do I own my website?",
    a: "If you buy it, yes. On a managed plan we own the site while you're on the plan, and you can buy it from us any time at the listed price. Your domain name is always yours.",
  },
  {
    q: "What's the difference between buying and managed?",
    a: "Buying costs more up front and less each month: you pay for the site once, then hosting or a care plan. Managed has no build fee: one monthly price covers the site, hosting, and changes.",
  },
  {
    q: "Do you work with businesses outside Columbia, MS?",
    a: "We're based in Columbia and work mostly across South Mississippi, but we're happy to hear from businesses farther out.",
  },
];

export const socialFaqs: FaqItem[] = [
  {
    q: "Which platforms do you manage?",
    a: "Starter covers two platforms, Facebook and Instagram. Standard and Full cover three: Facebook, Instagram, and TikTok.",
  },
  {
    q: "How many posts do I get each month?",
    a: "Starter is 8 to 12 posts a month. Standard and Full are 16 to 20 posts a month, plus stories and replies to comments.",
  },
  {
    q: "Do you run ads too?",
    a: "The Full plan includes ad campaign management. The ad spend itself is separate and goes straight to the platform.",
  },
  {
    q: "Do I have to sign up for a monthly plan?",
    a: "No. We also do one-off work: a social graphic is $30, an ad card is $35, and other tasks like content calendars and profile setup are $75 an hour.",
  },
  {
    q: "How much does social media management cost?",
    a: "Starter is $400 a month, Standard is $700, and Full is $1,000. Every plan starts with the first 30 days free.",
  },
  {
    q: "Do you manage social media for businesses in Columbia, MS?",
    a: "Yes. We're based in Columbia, MS and work with local small businesses across South Mississippi.",
  },
];

export const designFaqs: FaqItem[] = [
  {
    q: "How much does a logo cost?",
    a: "A logo is $300, with vector files and 2 rounds of changes included. A full brand package, with the logo, colors, fonts, a business card, and a social kit, is $800.",
  },
  {
    q: "What do other design jobs cost?",
    a: "Business cards and flyers are $75, a menu or sign is $150, a social graphic is $30, and an ad card is $35. Anything not listed is $75 an hour.",
  },
  {
    q: "Do I need to have my brand colors and photos ready?",
    a: "No. Send any assets you have, like photos, text, or brand colors, or let us create everything from scratch.",
  },
  {
    q: "What kind of files do I get?",
    a: "Finished files in the format the job needs: print-ready for cards and flyers, social-ready for posts and covers.",
  },
  {
    q: "Can you design menus and signage?",
    a: "Yes. Menus, yard signs, banners, and business cards, all delivered print-ready.",
  },
  {
    q: "Do you design for businesses in Columbia, MS?",
    a: "Yes. We're based in Columbia, MS and design for local small businesses across South Mississippi.",
  },
];

export const contractorFaqs: FaqItem[] = [
  {
    q: "Will a contractor website help me show up on Google?",
    a: "It gives you the foundation. Every page gets its own title and description, your sitemap goes to Google, and we build a page for each service and each town you serve so those searches have something to land on. Nobody can promise a ranking, but that structure is what we built for the contractors above.",
  },
  {
    q: "Do you build a separate page for every service?",
    a: "Yes. Each service gets its own page with what's included and common questions, so someone searching for excavation, painting, or brush cutting lands on a page about exactly that.",
  },
  {
    q: "Can customers request a quote from the website?",
    a: "Yes. Every site has a quote form that emails you, and your phone number is a tap-to-call button for people on their phones.",
  },
  {
    q: "How much does a contractor website cost?",
    a: "A business site with up to 10 pages is $1,800, or $150 a month managed with no build fee. Most contractor sites are bigger, since each service and town gets its own page, so those are quoted on scope at about $150 a page. Care plans start at $50 a month.",
  },
  {
    q: "Do you only work with contractors near Columbia?",
    a: "Our contractor clients are around Columbia and South Mississippi, and we're happy to hear from contractors farther out.",
  },
];
